import { useEffect, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import {
    firstFetchFiwareData,
    formatedFiwareDataType,
    legendType,
    updateFiwareData,
    walkCategoriesFiware
} from "../../api/fiware";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
    fiwareCatalogDataItemsAtom,
    fiwareDataAtom,
    legendTableAtom,
    mainMapAtom,
    subMapAtom,
    geoloniaBrokerStageAtom, fiwareFetchStatusesAtom
} from "../../atoms";
import { fetchFiwareDataType, fiwareDataType } from "../../types";
import { MenuSectionType } from "../../types/lib/types";
import { generateFiwarePolygonFilter, updateFiwarePolygonLayer } from "../../lib/smartCityStyle";

export const useFetchFiwareData = (catalog: MenuSectionType | undefined) => {
    const isFirstFetch = useRef<boolean>(true);
    const wsRef = useRef<ReconnectingWebSocket | null>(null);

    // Atoms
    const setFiwareData = useSetAtom(fiwareDataAtom);
    const [fiwareCatalogDataItems, setfiwareCatalogDataItems] = useAtom(fiwareCatalogDataItemsAtom);
    const setFiwareFetchStatuses = useSetAtom(fiwareFetchStatusesAtom);
    const setFiwareLegend = useSetAtom(legendTableAtom);
    const mainMap = useAtomValue(mainMapAtom);
    const subMap = useAtomValue(subMapAtom);
    const geoloniaBrokerStage = useAtomValue(geoloniaBrokerStageAtom);

    // 各catalogItemに対応するエンドポイントを作成
    const fiwareEndpoints = fiwareCatalogDataItems ? Object.fromEntries(
        fiwareCatalogDataItems.map(item => [
            item.dataId,
            `https://api-ws-admin.geolonia.com/${geoloniaBrokerStage}/channels/yaizu-${item.dataId}/messages`
        ])
    ) : {};

    // websocketの設定
    const ws_config = fiwareCatalogDataItems 
        ? fiwareCatalogDataItems
            .map(item => ({ 
                action: "subscribe", 
                channel: `yaizu-${item.dataId}` 
            }))
        : [];

    const updateFiwareFetchStatuses = (fiwareData?: formatedFiwareDataType, legendData?: legendType) => {
        const statuses: { [key: string]: boolean } = {};
        fiwareCatalogDataItems?.forEach((item) => {
            const dataId = item.dataId;
            // データによっては、dataが空配列になる可能性があるため、legendも確認する
            const hasData = !!(fiwareData && fiwareData[dataId]);
            const hasLegend = !!(legendData && legendData[dataId]);
            statuses[dataId] = hasData || hasLegend;
        });
        setFiwareFetchStatuses(statuses);
    }


    /* ********************
     *  カタログ周りのデータを取得
     * ********************/
    useEffect(() => {
        if (!catalog) return;

        const fiwareCatalogRaw = Object.values(catalog).find(
            (menu) => menu.metadata?.dataType === 'fiware'
        );

        if (!fiwareCatalogRaw) return;

        setfiwareCatalogDataItems([
            ...walkCategoriesFiware(fiwareCatalogRaw.items as unknown as { [key: string]: fiwareDataType; })
        ]);
    }, [catalog]);


    /* ********************
     *  fiwareデータを取得・セット
     * ********************/
    useEffect(() => {
        if (!fiwareCatalogDataItems || !fiwareEndpoints) return;

        // 初回データフェッチ
        const fetchInitialData = async () => {
            if (isFirstFetch.current) {
                const {fiwareData, legendData} = await firstFetchFiwareData(fiwareEndpoints);
                updateFiwareFetchStatuses(fiwareData, legendData);
                setFiwareData(fiwareData);
                setFiwareLegend((prev) => {
                    return prev ? {...prev, ...legendData} : legendData;
                });
                isFirstFetch.current = false;
            }
        };

        // WebSocket接続の設定
        const setupWebSocket = () => {
            if (wsRef.current) return;  // 既に接続がある場合は何もしない

            wsRef.current = new ReconnectingWebSocket(`wss://api-ws.geolonia.com/${geoloniaBrokerStage}`);

            // 接続時の処理
            wsRef.current.addEventListener('open', () => {
                ws_config.forEach(config => {
                    wsRef.current?.send(JSON.stringify(config));
                });
            });

            // エラー処理
            wsRef.current.addEventListener('error', (e) => {
                console.error('WebSocket error:', e);
            });

            // メッセージ受信時の処理
            wsRef.current.addEventListener('message', (message) => {
                const res = JSON.parse(message.data);
                if (typeof res.msg === 'string' || !res.msg?.data) return;
                const json: fetchFiwareDataType = res.msg.data;
                if (!json) return;
                    
                // fiwaredataを更新
                setFiwareData(currentFiwareData => {
                    const { newFiwareData, isUpdated } = updateFiwareData( res.id, currentFiwareData, json );
                    if(!isUpdated) { return currentFiwareData; }

                    const targetItem = fiwareCatalogDataItems.find(item => item.dataId === res.id);
                    let colorFilter = undefined;
                    let filter = undefined;
    
                    // fiwareの場合、tileIdがある場合は、フィルターを追加
                    if(targetItem && 'tileId' in targetItem) {
                        const res = generateFiwarePolygonFilter(newFiwareData, targetItem.dataId);
                        colorFilter = res?.fillColorExpression;
                        filter = res?.filter;
                        updateFiwarePolygonLayer(mainMap, targetItem.id, colorFilter, filter);
                        updateFiwarePolygonLayer(subMap, targetItem.id, colorFilter, filter);
                    }
                    return newFiwareData;
                });

            });
        };

        // 初期データ取得とWebSocket接続を実行
        fetchInitialData();
        setupWebSocket();

        // クリーンアップ
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [fiwareCatalogDataItems, mainMap, subMap]);
};
